import {
  Building2,
  ChevronDown,
  Compass,
  Database,
  Eye,
  Star,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ClientSearchIdentity,
  ClientSearchTypeBadge,
} from './ClientSearchIdentity';
import { SearchSecurityTypeChip } from './SearchTypeChip';
import {
  searchCodeStyle,
  searchIdentityLayout,
  searchNameStyle,
} from './searchAppearance';
import type { SearchItem } from '../model/searchModel';
import { AssetLogo } from '@/components/tables/tableParts';
import { CountryFlag } from '@/shared/components/CountryFlag';
import {
  instrumentExchange,
  instrumentVenue,
} from '@/features/Instruments/instrumentMetadata';
import { ExchangeLogo } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/components/EligibilityRules/SecurityTypeClassification/ExchangeLogo';
import { cn } from '@/lib/utils';

export function SearchResultRow({
  item,
  id,
  active,
  onHover,
  onOpen,
  onToggle,
  onPin,
  onPreview,
  expanded,
  pinned,
  canPin,
}: {
  item: SearchItem;
  id: string;
  active: boolean;
  onHover: () => void;
  onOpen: () => void;
  onToggle: () => void;
  onPin: () => void;
  onPreview: () => void;
  expanded: boolean;
  pinned: boolean;
  canPin: boolean;
}) {
  const { t } = useTranslation('app-shell');
  const text = (key: string, value: string) =>
    t(`header.${key}`, { defaultValue: value });
  const security = item.kind === 'security' ? item : null;
  const venue = security ? instrumentVenue(security.instrument) : undefined;
  const exchange = security ? instrumentExchange(security.instrument) : '';
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      className={cn(
        'group/result mx-1 flex min-w-0 items-center gap-1 rounded-sm px-3 transition-colors hover:bg-primary/2 motion-reduce:transition-none',
        active && 'bg-primary/4',
        security?.child && 'bg-muted/15 pl-7',
      )}
      onMouseEnter={onHover}
      onClick={(event) => {
        if (!(event.target as Element).closest('button')) onOpen();
      }}
    >
      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onOpen}
        aria-label={`${item.code} ${item.label}${security ? ` ${security.instrument.securityType === 'Stock' ? 'Equity' : (security.instrument.securityType ?? '')} ${exchange}` : ''}`}
        title={item.label}
        className={searchIdentityLayout}
      >
        {item.kind === 'client' ? (
          <ClientSearchIdentity client={item.client} showCode />
        ) : security ? (
          <>
            <AssetLogo
              ticker={item.code}
              mic={venue?.mic}
              size="sm"
              className="row-span-2 p-0 sm:row-span-1"
            />
            <span className={searchCodeStyle}>{item.code}</span>
            <span className={cn(searchNameStyle, 'flex-wrap gap-y-1 sm:py-2')}>
              <span className="min-w-0 truncate">{item.label}</span>
              <SearchSecurityTypeChip type={security.instrument.securityType} />
            </span>
          </>
        ) : (
          <>
            <span className="row-span-2 flex size-8 shrink-0 items-center justify-center bg-muted/50 text-muted-foreground ring-1 ring-inset ring-border/30 sm:row-span-1">
              {item.kind === 'strategy' ? (
                <Database className="size-4" />
              ) : (
                <Compass className="size-4" />
              )}
            </span>
            <span className={searchCodeStyle}>{item.code}</span>
            <span className={cn(searchNameStyle, 'truncate')}>
              {item.label}
            </span>
          </>
        )}
      </button>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          tabIndex={active ? 0 : -1}
          aria-label={`${text('search_preview', 'Preview')} ${item.label}`}
          aria-keyshortcuts="Alt+Space"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onPreview}
          className={cn(
            'rounded p-1.5 text-muted-foreground hover:bg-primary/6 focus-visible:outline-2 focus-visible:outline-ring sm:opacity-0 sm:group-hover/result:opacity-100 sm:group-focus-within/result:opacity-100',
            active && 'sm:opacity-100',
          )}
        >
          <Eye className="size-3.5" />
        </button>
        {canPin && (
          <button
            type="button"
            tabIndex={active ? 0 : -1}
            aria-label={`${pinned ? text('search_unpin', 'Unpin') : text('search_pin', 'Pin')} ${item.label}`}
            aria-pressed={pinned}
            aria-keyshortcuts="Alt+P"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onPin}
            className={cn(
              'rounded p-1.5 text-muted-foreground hover:bg-primary/6 focus-visible:outline-2 focus-visible:outline-ring sm:opacity-0 sm:group-hover/result:opacity-100 sm:group-focus-within/result:opacity-100',
              (active || pinned) && 'sm:opacity-100',
            )}
          >
            <Star
              className={cn(
                'size-3.5',
                pinned && 'fill-primary/20 text-primary',
              )}
            />
          </button>
        )}
      </div>
      {security && (
        <span className="flex w-11 shrink-0 justify-center">
          {security.children.length > 0 && (
            <button
              type="button"
              tabIndex={active ? 0 : -1}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} listings for ${item.code} ${item.label}`}
              aria-expanded={expanded}
              onClick={onToggle}
              className="flex h-[22px] shrink-0 items-center gap-1 rounded px-1 text-xs tabular-nums text-muted-foreground hover:bg-primary/6 focus-visible:outline-2 focus-visible:outline-ring"
            >
              +{security.children.length}
              <ChevronDown className={cn('size-3', expanded && 'rotate-180')} />
            </button>
          )}
        </span>
      )}
      {security ? (
        <span className="ml-1 flex w-20 shrink-0 items-center justify-end gap-2 text-xs sm:w-40 sm:text-[13px]">
          {venue && (
            <CountryFlag
              code={venue.country}
              className="h-4 w-6 shrink-0 rounded-xs object-cover ring-1 ring-border/40"
            />
          )}
          <span className="min-w-0 truncate" title={venue?.label || exchange}>
            {venue?.label || exchange || '—'}
          </span>
          {venue ? (
            <ExchangeLogo
              mic={venue.mic}
              domain={venue.domain}
              className="hidden h-8 w-8 sm:inline-flex"
            />
          ) : (
            <Building2
              aria-label={text(
                'search_venue_missing',
                'Listing venue not supplied',
              )}
              className="hidden size-8 shrink-0 rounded-full border border-border/40 bg-muted/30 p-2 text-muted-foreground sm:block"
            />
          )}
        </span>
      ) : (
        <span className="ml-1 flex w-20 shrink-0 justify-end sm:w-[204px]">
          {item.kind === 'client' && (
            <ClientSearchTypeBadge status={item.client.status} />
          )}
        </span>
      )}
    </div>
  );
}
